import { usePartner } from "../../context/PartnerContext";
import React, { useState, useEffect } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import usePageTitle from "../../hooks/usePageTitle";

const gradeColor = g => g?.startsWith("A")?"var(--success)":g?.startsWith("B")?"var(--accent)":g?.startsWith("C")?"var(--warning)":"var(--danger)";
const fmt2 = v => { const n=Number(v)||0; if(Math.abs(n)>=1000) return (n/1000).toFixed(2)+"K"; return n.toFixed(2); };
const fmtPct = v => (Number(v)||0).toFixed(1)+"%";

function BlurLock({ label }) {
  return (
    <div style={{position:"relative",borderRadius:10,overflow:"hidden",border:"1px solid var(--border)",background:"var(--bg-surface)"}}>
      <div style={{filter:"blur(4px)",opacity:0.4,padding:16}}>
        {[1,2,3,4].map(i=>(
          <div key={i} style={{height:12,background:"var(--border)",borderRadius:4,marginBottom:8,width:["80%","60%","90%","70%"][i-1]}}/>
        ))}
      </div>
      <div style={{position:"absolute",inset:0,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:8}}>
        <div style={{fontSize:18}}></div>
        <div style={{fontSize:12,fontWeight:700,color:"var(--text-main)"}}>{label}</div>
        <div style={{fontSize:11,color:"var(--text-muted)"}}>Register free to unlock</div>
      </div>
    </div>
  );
}

export default function PublicAnalystPage() {
  const { setId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const refCode = searchParams.get("ref") || "";

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  const { appName, tagline } = usePartner();
  usePageTitle(data ? `${data.set?.name || "Signal Plan"}` : "Signal Plan");

  useEffect(() => {
    if (refCode) localStorage.setItem("ca_ref", refCode);
  }, [refCode]);

  useEffect(() => {
    fetch(`/api/public/analyst-profile/${setId}`)
      .then(r => r.json())
      .then(d => { if (d.ok) setData(d); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [setId]);

  const registerUrl = `/register${refCode ? `?ref=${refCode}` : ""}`;

  const copyShareUrl = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) return (
    <div style={{minHeight:"100vh",background:"var(--bg)",display:"flex",alignItems:"center",justifyContent:"center",color:"var(--text-muted)",fontSize:14}}>
      Loading signal plan...
    </div>
  );

  if (!data) return (
    <div style={{minHeight:"100vh",background:"var(--bg)",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:12}}>
      <div style={{fontSize:32}}></div>
      <div style={{fontSize:16,fontWeight:700,color:"var(--text-main)"}}>Signal plan not found</div>
      <button onClick={()=>navigate("/")} style={{padding:"8px 20px",borderRadius:8,background:"var(--accent)",color:"#fff",border:"none",cursor:"pointer",fontSize:13}}>Back to Home</button>
    </div>
  );

  const set   = data.set || {};
  const score = set.alphaScore || set.alpha_score || 0;
  const grade = set.alphaGrade || set.alpha_grade || "—";
  const gc    = gradeColor(grade);
  const stats = data.stats || {};
  const wr    = stats.winRate || set.win_rate || 0;
  const pillars = data.pillars || [];
  const flags   = data.flags?.filter(f=>f.severity!=="INFO") || [];

  return (
    <div style={{minHeight:"100vh",background:"var(--bg)",color:"var(--text-main)"}}>
      {/* NAV */}
      <header style={{background:"var(--bg-surface)",borderBottom:"1px solid var(--border)",padding:"0 24px",height:54,display:"flex",alignItems:"center",justifyContent:"space-between",position:"sticky",top:0,zIndex:50}}>
        <a href="/" style={{textDecoration:"none"}}>
          <div style={{fontWeight:800,fontSize:16,color:"var(--text-main)"}}>{appName === "CrunchAlpha" ? (<>Crunch<span style={{color:"var(--accent)"}}>Alpha</span></>) : appName}</div>
          {tagline && <div style={{fontSize:9,color:"var(--accent)",fontWeight:600}}>{tagline}</div>}
        </a>
        <div style={{display:"flex",gap:10,alignItems:"center"}}>
          <button onClick={copyShareUrl}
            style={{padding:"6px 14px",borderRadius:7,background:"var(--bg-elevated)",border:"1px solid var(--border)",color:"var(--text-muted)",fontSize:12,cursor:"pointer"}}>
            {copied ? " Copied!" : " Share"}
          </button>
          <a href={registerUrl}
            style={{padding:"7px 16px",borderRadius:7,background:"var(--accent)",color:"#fff",fontSize:13,fontWeight:600,textDecoration:"none"}}>
            Get started free →
          </a>
        </div>
      </header>

      <div style={{maxWidth:780,margin:"0 auto",padding:"28px 20px 48px"}}>

        {/* HEADER */}
        <div style={{background:"var(--bg-surface)",border:"1px solid var(--border)",borderRadius:14,padding:"24px 24px 20px",marginBottom:16}}>
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",flexWrap:"wrap",gap:12}}>
            <div>
              <div style={{display:"flex",alignItems:"center",gap:8,marginBottom:6,flexWrap:"wrap"}}>
                <div style={{fontSize:22,fontWeight:800}}>{set.name || "Signal Plan"}</div>
                <span style={{fontSize:10,fontWeight:700,padding:"2px 8px",borderRadius:8,
                  background:"rgba(78,154,241,0.1)",color:"var(--accent)",border:"1px solid rgba(78,154,241,0.25)"}}>
                     Analyst
                </span>
              </div>
              <div style={{fontSize:12,color:"var(--text-muted)"}}>
  {[set.analystName, set.market, set.style, `${stats.totalSignals||set.total_signals||0} signals`, `${stats.closedSignals||0} closed`].filter(Boolean).join(" · ")}
              </div>
            </div>
            <div style={{textAlign:"center"}}>
              <div style={{fontSize:10,color:"var(--text-faint)",marginBottom:3}}>AlphaScore</div>
              <div style={{width:68,height:68,borderRadius:"50%",border:`4px solid ${gc}`,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",background:"rgba(255,255,255,0.02)"}}>
                <div style={{fontSize:16,fontWeight:900,color:gc}}>{grade}</div>
                <div style={{fontSize:11,fontWeight:700,color:gc}}>{fmt2(score)}</div>
              </div>
              <div style={{fontSize:10,color:"var(--text-faint)",marginTop:5}}>
                Risk: <span style={{fontWeight:700,color:"var(--text-muted)"}}>{set.riskLevel||"—"}</span>
              </div>
            </div>
          </div>
          {/* Win Rate bar */}
          <div style={{margin:"16px 0 4px"}}>
            <div style={{display:"flex",justifyContent:"space-between",fontSize:11,color:"var(--text-faint)",marginBottom:5}}>
              <span>Win Rate</span><span style={{fontWeight:700,color:"var(--text-main)"}}>{fmtPct(wr)}</span>
            </div>
            <div style={{height:5,background:"var(--bg-elevated)",borderRadius:999,overflow:"hidden"}}>
              <div style={{height:"100%",width:`${wr}%`,background:"var(--accent)",borderRadius:999}}/>
            </div>
          </div>
        </div>

        {/* KEY METRICS */}
        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(130px,1fr))",gap:10,marginBottom:16}}>
          {[
            { l:"Win Rate",      v:fmtPct(wr),                                      c:"var(--text-main)" },
            { l:"Total Signals", v:String(stats.totalSignals||set.total_signals||0),   c:"var(--text-main)" },
            { l:"Closed Signals",v:String(stats.closedSignals||set.closed_signals||0), c:"var(--text-main)" },
            { l:"Avg RR",        v:fmt2(data.metrics?.avgRR||0)+"R",                   c:"var(--accent)"    },
          ].map(m=>(
            <div key={m.l} style={{background:"var(--bg-surface)",border:"1px solid var(--border)",borderRadius:10,padding:"14px 14px"}}>
              <div style={{fontSize:10,color:"var(--text-faint)",marginBottom:4}}>{m.l}</div>
              <div style={{fontSize:20,fontWeight:800,color:m.c}}>{m.v}</div>
            </div>
          ))}
        </div>

        {/* ALPHARANK PILLARS */}
        {pillars.length > 0 && (
          <div style={{background:"var(--bg-surface)",border:"1px solid var(--border)",borderRadius:14,padding:20,marginBottom:16}}>
            <div style={{fontSize:13,fontWeight:700,marginBottom:14}}>AlphaRank™ Breakdown</div>
            {pillars.map(p=>(
              <div key={p.code} style={{marginBottom:10}}>
                <div style={{display:"flex",justifyContent:"space-between",marginBottom:3}}>
                  <span style={{fontSize:12,color:"var(--text-muted)"}}>{p.name}</span>
                  <span style={{fontSize:12,fontWeight:700,color:"var(--text-main)"}}>{p.score?.toFixed(0)}</span>
                </div>
                <div style={{height:4,background:"var(--bg-elevated)",borderRadius:999,overflow:"hidden"}}>
                  <div style={{height:"100%",width:`${p.score}%`,background:"var(--accent)",borderRadius:999,opacity:0.7+p.score/333}}/>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* RISK FLAGS */}
        {flags.length > 0 && (
          <div style={{background:"var(--bg-surface)",border:"1px solid var(--border)",borderRadius:14,padding:20,marginBottom:16}}>
            <div style={{fontSize:13,fontWeight:700,marginBottom:12}}>Risk Flags</div>
            {flags.map((f,i)=>(
              <div key={i} style={{display:"flex",gap:10,alignItems:"flex-start",marginBottom:10,
                padding:"10px 12px",borderRadius:8,
                background:f.severity==="MAJOR"?"rgba(239,68,68,0.06)":"rgba(234,179,8,0.06)",
                border:`1px solid ${f.severity==="MAJOR"?"rgba(239,68,68,0.2)":"rgba(234,179,8,0.2)"}`}}>
                <span style={{fontSize:14,flexShrink:0,marginTop:1}}>{f.severity==="MAJOR"?"    ":"ℹ  "}</span>
                <div>
                  <div style={{fontSize:12,fontWeight:700,color:"var(--text-main)",marginBottom:2}}>{f.title}</div>
                  <div style={{fontSize:11,color:"var(--text-muted)",lineHeight:1.6}}>{f.desc}</div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* LOCKED */}
        <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:20}}>
          <BlurLock label="Full Signal History"/>
          <BlurLock label="Live Active Signals"/>
        </div>

        {/* CTA */}
        <div style={{background:"rgba(78,154,241,0.07)",border:"1px solid rgba(78,154,241,0.2)",borderRadius:14,padding:"32px 24px",textAlign:"center"}}>
          <div style={{fontSize:20,fontWeight:800,marginBottom:8,letterSpacing:"-0.02em"}}>Follow this signal plan</div>
          <div style={{fontSize:13,color:"var(--text-muted)",lineHeight:1.7,maxWidth:360,margin:"0 auto 20px"}}>
            Signals execute automatically with risk-normalized sizing. Your funds stay in your own broker — always.
          </div>
          <div style={{display:"flex",gap:10,justifyContent:"center",flexWrap:"wrap"}}>
            <a href={registerUrl}
              style={{padding:"12px 28px",borderRadius:8,background:"var(--accent)",color:"#fff",fontSize:14,fontWeight:700,textDecoration:"none"}}>
              Register free to follow →
            </a>
            <a href="/"
              style={{padding:"11px 20px",borderRadius:8,background:"transparent",border:"1px solid var(--border)",color:"var(--text-muted)",fontSize:13,textDecoration:"none"}}>
              Learn more
            </a>
          </div>
          <div style={{fontSize:11,color:"var(--text-faint)",marginTop:12}}>
            Performance fee only when you profit · Non-custodial · Free to join
          </div>
        </div>

      </div>

      <footer style={{borderTop:"1px solid var(--border)",padding:"16px 24px",textAlign:"center",fontSize:11,color:"var(--text-faint)"}}>
        <strong style={{color:"var(--text-main)"}}>Crunch<span style={{color:"var(--accent)"}}>Alpha</span></strong>
        {" "}© 2026 · Non-custodial · Trading involves risk ·{" "}
        <a href="/legal/risk" style={{color:"var(--text-faint)",textDecoration:"none"}}>Risk Disclosure</a>
      </footer>
    </div>
  );
}
